import { useState, useCallback } from "react";
import * as api from "../api";

// Calendarios de empresa (owner_id null, solo admin edita) + calendarios personales.
// Los eventos llegan ya expandidos por año desde el backend: las recurrencias
// anuales vienen resueltas, así que aquí se tratan como filas planas por fecha.
export function useCalendarios(token, showToast) {
  const [calendarios, setCalendarios] = useState([]);
  const [eventosCal,  setEventosCal ] = useState([]);

  const loadCalendarios = useCallback(async () => {
    if (!token) return;
    try {
      const data = await api.getCalendarios();
      if (Array.isArray(data)) setCalendarios(data);
    } catch (e) {
      showToast("Error al cargar los calendarios", "error");
    }
  }, [token]); // eslint-disable-line

  const loadEventosCal = useCallback(async (from, to) => {
    if (!token) return;
    try {
      const data = await api.getEventosCal(from, to);
      if (Array.isArray(data)) setEventosCal(data);
    } catch (e) {
      showToast("Error al cargar los eventos del calendario", "error");
    }
  }, [token]); // eslint-disable-line

  // Tras cualquier escritura se recargan los eventos: crear un evento anual o con
  // rango genera varias filas expandidas que no se pueden deducir en cliente.
  const refetch = () => loadEventosCal();

  const createCalendario = async (d) => {
    try {
      const cal = await api.createCalendario(d);
      setCalendarios(p => [...p, cal]);
      showToast("Calendario creado");
      return cal;
    } catch (e) { showToast(e.message || "Error al crear el calendario", "error"); }
  };

  const updateCalendario = async (id, d) => {
    try {
      const cal = await api.updateCalendario(id, d);
      setCalendarios(p => p.map(c => String(c.id) === String(id) ? { ...c, ...cal } : c));
      await refetch();               // el color del calendario tiñe sus eventos
      showToast("Calendario actualizado");
      return cal;
    } catch (e) { showToast(e.message || "Error al actualizar el calendario", "error"); }
  };

  const deleteCalendario = async (id, force = false) => {
    try {
      const r = await api.deleteCalendario(id, force);
      setCalendarios(p => p.filter(c => String(c.id) !== String(id)));
      setEventosCal(p => p.filter(e => String(e.calendario_id) !== String(id)));
      showToast("Calendario eliminado", "warn");
      return r;
    } catch (e) {
      // 409: el backend avisa de cuántos eventos se perderían — lo gestiona la UI
      if (e.message && /evento\(s\)/.test(e.message)) throw e;
      showToast(e.message || "Error al eliminar el calendario", "error");
    }
  };

  const createEvento = async (d) => {
    try {
      await api.createEventoCal(d);
      await refetch();
      showToast("Evento añadido");
    } catch (e) { showToast(e.message || "Error al crear el evento", "error"); }
  };

  const updateEvento = async (id, d) => {
    try {
      await api.updateEventoCal(id, d);
      await refetch();
      showToast("Evento actualizado");
    } catch (e) { showToast(e.message || "Error al actualizar el evento", "error"); }
  };

  const deleteEvento = async (id) => {
    try {
      await api.deleteEventoCal(id);
      setEventosCal(p => p.filter(e => String(e.id) !== String(id)));
      showToast("Evento eliminado", "warn");
    } catch (e) { showToast(e.message || "Error al eliminar el evento", "error"); }
  };

  const importarCalendario = async (id, text, anual = false) => {
    try {
      const r = await api.importarCalendario(id, text, anual);
      await Promise.all([refetch(), loadCalendarios()]);
      const errs = (r.errores || []).length;
      showToast(`${r.creados} evento(s) importado(s)${errs ? ` · ${errs} línea(s) con error` : ""}`,
                errs ? "warn" : "ok");
      return r;
    } catch (e) { showToast(e.message || "Error al importar", "error"); }
  };

  const duplicarAnio = async (id, fromYear, toYear) => {
    try {
      const r = await api.duplicarAnioCalendario(id, fromYear, toYear);
      await Promise.all([refetch(), loadCalendarios()]);
      showToast(`${r.copiados} evento(s) copiado(s) a ${toYear}${r.omitidos ? ` · ${r.omitidos} ya existía(n)` : ""}`);
      return r;
    } catch (e) { showToast(e.message || "Error al duplicar el año", "error"); }
  };

  return {
    calendarios, setCalendarios, eventosCal, setEventosCal,
    loadCalendarios, loadEventosCal,
    createCalendario, updateCalendario, deleteCalendario,
    createEvento, updateEvento, deleteEvento,
    importarCalendario, duplicarAnio,
  };
}
